import {
  ZadCategoryRef,
  ZadPopulatedProductType,
  ZadProduct,
  ZadProductType,
  ZadProductWritePayload,
} from '../../../models/zad'
import {toZadPackagingWritePayload} from './packagingHelpers'
import {getZadCategoryId, getZadCategoryLabel, normalizeZadCode} from './categoryHelpers'
import {resolveZadUserMessage, ZadApiError} from './errors'
import {parseZadStockQuantity} from './stockHelpers'

export function roundZadMoney3(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000
}

export function formatZadTnd(value: number | string | null | undefined): string {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return '0,000 DT'
  const rounded = roundZadMoney3(numeric)
  const [intPart, decPart] = rounded.toFixed(3).split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${grouped},${decPart} DT`
}

export function preserveZadBarcode(value: unknown): string {
  if (value == null) return ''
  return String(value)
}

export function isZadProductArchived(
  product?: Pick<ZadProduct, 'archive' | 'isActive'> | null
): boolean {
  return product?.archive?.isArchived === true
}

export function canOfferZadProduct(
  product?: Pick<ZadProduct, 'archive' | 'isActive'> | null
): boolean {
  if (!product) return false
  if (product.isActive === false) return false
  if (isZadProductArchived(product)) return false
  return true
}

export function zadProductArchiveReasonMessageId(reason?: string | null): string {
  const map: Record<string, string> = {
    PURCHASE_CANCELLED: 'ZAD.PRODUCTS.ARCHIVE_REASON_PURCHASE',
    MANUAL: 'ZAD.PRODUCTS.ARCHIVE_REASON_MANUAL',
  }
  return map[String(reason || '')] || 'ZAD.PRODUCTS.ARCHIVE_REASON_PURCHASE'
}

export function parseZadPriceInput(
  value: string | number | null | undefined,
  options: {required?: boolean; defaultValue?: number} = {}
): {ok: true; value: number} | {ok: false} {
  const required = options.required === true
  const defaultValue = options.defaultValue ?? 0
  if (value === undefined || value === null || value === '') {
    if (required) return {ok: false}
    return {ok: true, value: defaultValue}
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return {ok: false}
    return {ok: true, value}
  }
  const normalized = String(value).trim().replace(',', '.')
  if (!normalized) {
    if (required) return {ok: false}
    return {ok: true, value: defaultValue}
  }
  if (!/^\d+(\.\d{1,3})?$/.test(normalized)) return {ok: false}
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric) || numeric < 0) return {ok: false}
  return {ok: true, value: numeric}
}

export function getZadProductTypeId(
  productType: ZadProduct['productType'] | ZadProductType | string | null | undefined
): string {
  if (!productType) return ''
  if (typeof productType === 'string') return productType
  if (typeof productType === 'object' && productType._id) return String(productType._id)
  return ''
}

export function getZadProductTypeLabel(
  productType: ZadProduct['productType'] | ZadProductType | string | null | undefined
): string {
  if (!productType || typeof productType === 'string') return ''
  return productType.name || ''
}

export function getZadProductTypeCategory(
  productType: ZadProduct['productType'] | ZadProductType | null | undefined
): ZadCategoryRef | string | null {
  if (!productType || typeof productType === 'string') return null
  return productType.category || null
}

export function getZadProductCategoryId(
  product: Pick<ZadProduct, 'productType'> | null | undefined
): string {
  return getZadCategoryId(getZadProductTypeCategory(product?.productType))
}

export function getZadProductCategoryLabel(
  product: Pick<ZadProduct, 'productType'> | null | undefined
): string {
  return getZadCategoryLabel(getZadProductTypeCategory(product?.productType))
}

export function filterProductTypesByCategory(
  productTypes: ZadProductType[] = [],
  categoryId: string
): ZadProductType[] {
  if (!categoryId) return productTypes
  return productTypes.filter((type) => getZadCategoryId(type.category) === categoryId)
}

export function toZadProductPayload(values: {
  name: string
  code: string
  barcode?: string
  productType: ZadProduct['productType'] | string
  purchasePrice: string | number
  salePrice: string | number
  publisher?: string
  brand?: string
  description?: string
  packagingEnabled?: boolean
  packagingWasEnabled?: boolean
  packaging?: ZadProductWritePayload['packaging']
  packagingBaseUnit?: string
  packagingPackageUnit?: string
  packagingUnitsPerPackage?: string | number
  packagingAllowUnitSale?: boolean
  packagingAllowPackageSale?: boolean
  packagingPackageSalePrice?: string | number
  packagingUnitBarcode?: string
  packagingPackageBarcode?: string
}): ZadProductWritePayload {
  const purchase = parseZadPriceInput(values.purchasePrice, {required: false, defaultValue: 0})
  const sale = parseZadPriceInput(values.salePrice, {required: true})
  const barcode = preserveZadBarcode(values.barcode).trim()
  const packaging = toZadPackagingWritePayload(values)
  return {
    name: String(values.name || '').trim(),
    code: normalizeZadCode(values.code),
    barcode,
    productType: getZadProductTypeId(values.productType),
    purchasePrice: purchase.ok ? purchase.value : 0,
    salePrice: sale.ok ? sale.value : Number.NaN,
    publisher: values.publisher != null ? String(values.publisher).trim() : '',
    brand: values.brand != null ? String(values.brand).trim() : '',
    description: values.description != null ? String(values.description).trim() : '',
    ...(packaging ? {packaging} : {}),
  }
}

export type ZadProductFormValues = {
  name: string
  code: string
  barcode: string
  category: string
  productType: string
  purchasePrice: string
  salePrice: string
  publisher: string
  brand: string
  description: string
  packagingEnabled?: boolean
  packagingWasEnabled?: boolean
  packagingBaseUnit?: string
  packagingPackageUnit?: string
  packagingUnitsPerPackage?: string
  packagingAllowUnitSale?: boolean
  packagingAllowPackageSale?: boolean
  packagingPackageSalePrice?: string
  packagingUnitBarcode?: string
  packagingPackageBarcode?: string
}

export function validateZadProductForm(values: ZadProductFormValues): string | null {
  if (!String(values.name || '').trim()) return 'ZAD.COMMON.NAME_REQUIRED'
  if (!String(values.code || '').trim()) return 'ZAD.COMMON.CODE_REQUIRED'
  if (!String(values.productType || '').trim()) return 'ZAD.PRODUCTS.TYPE_REQUIRED'
  const purchase = parseZadPriceInput(values.purchasePrice, {required: false, defaultValue: 0})
  if (!purchase.ok) return 'ZAD.PRODUCTS.PURCHASE_PRICE_INVALID'
  const sale = parseZadPriceInput(values.salePrice, {required: true})
  if (!sale.ok) return 'ZAD.PRODUCTS.SALE_PRICE_REQUIRED'
  if (values.packagingEnabled) {
    const units = parseZadStockQuantity(values.packagingUnitsPerPackage)
    if (!units.ok || units.value < 2) return 'ZAD.PACKAGING.INVALID'
    if (!values.packagingAllowUnitSale && !values.packagingAllowPackageSale) return 'ZAD.PACKAGING.INVALID'
    if (values.packagingAllowPackageSale) {
      const packPrice = parseZadPriceInput(values.packagingPackageSalePrice, {required: true})
      if (!packPrice.ok) return 'ZAD.PACKAGING.PACKAGE_PRICE_REQUIRED'
    }
    const unitBarcode = preserveZadBarcode(values.packagingUnitBarcode).trim()
    const packageBarcode = preserveZadBarcode(values.packagingPackageBarcode).trim()
    if (unitBarcode && packageBarcode && unitBarcode === packageBarcode) return 'ZAD.PACKAGING.AMBIGUOUS_BARCODE'
    if (packageBarcode && preserveZadBarcode(values.barcode).trim() === packageBarcode) {
      return 'ZAD.PACKAGING.AMBIGUOUS_BARCODE'
    }
  }
  return null
}

export function canCreateZadProduct(productTypes: ZadProductType[] = []): boolean {
  return productTypes.some((type) => type.isActive)
}

export function resolveZadProductMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  if (err.errorCode === 'DUPLICATE_CODE') {
    return {
      i18nId: 'ZAD.PRODUCTS.DUPLICATE_CODE',
      fallback: 'A product with this code already exists.',
    }
  }
  if (err.errorCode === 'BARCODE_ALREADY_EXISTS') {
    return {
      i18nId: 'ZAD.PRODUCTS.BARCODE_ALREADY_EXISTS',
      fallback: 'This product already has a barcode.',
    }
  }
  if (err.errorCode === 'PRODUCT_NOT_FOUND') {
    return {
      i18nId: 'ZAD.PRODUCTS.NOT_FOUND',
      fallback: 'Product was not found.',
    }
  }
  const packagingMap: Record<string, string> = {
    INVALID_PACKAGING: 'ZAD.PACKAGING.INVALID',
    PACKAGE_PRICE_REQUIRED: 'ZAD.PACKAGING.PACKAGE_PRICE_REQUIRED',
    AMBIGUOUS_BARCODE: 'ZAD.PACKAGING.AMBIGUOUS_BARCODE',
    DUPLICATE_BARCODE: 'ZAD.PRODUCTS.DUPLICATE_BARCODE',
  }
  if (err.errorCode && packagingMap[err.errorCode]) {
    return {i18nId: packagingMap[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function toZadProductFormValues(product?: ZadProduct | null): ZadProductFormValues {
  if (!product) {
    return {
      name: '',
      code: '',
      barcode: '',
      category: '',
      productType: '',
      purchasePrice: '0',
      salePrice: '',
      publisher: '',
      brand: '',
      description: '',
      packagingEnabled: false,
      packagingWasEnabled: false,
      packagingBaseUnit: 'PIECE',
      packagingPackageUnit: 'BOX',
      packagingUnitsPerPackage: '12',
      packagingAllowUnitSale: true,
      packagingAllowPackageSale: true,
      packagingPackageSalePrice: '',
      packagingUnitBarcode: '',
      packagingPackageBarcode: '',
    }
  }
  const populated = product.productType as ZadPopulatedProductType | string
  const pack = product.packaging
  return {
    name: product.name || '',
    code: product.code || '',
    barcode: preserveZadBarcode(product.barcode),
    category: getZadProductCategoryId(product),
    productType: getZadProductTypeId(populated),
    purchasePrice:
      product.purchasePrice == null ? '0' : String(product.purchasePrice),
    salePrice: product.salePrice == null ? '' : String(product.salePrice),
    publisher: product.publisher || '',
    brand: product.brand || '',
    description: product.description || '',
    packagingEnabled: pack?.enabled === true,
    packagingWasEnabled: pack?.enabled === true,
    packagingBaseUnit: pack?.baseUnit || 'PIECE',
    packagingPackageUnit: pack?.packageUnit || 'BOX',
    packagingUnitsPerPackage: pack?.unitsPerPackage != null ? String(pack.unitsPerPackage) : '12',
    packagingAllowUnitSale: pack?.allowUnitSale !== false,
    packagingAllowPackageSale: pack?.allowPackageSale !== false,
    packagingPackageSalePrice: pack?.packageSalePrice != null ? String(pack.packageSalePrice) : '',
    packagingUnitBarcode: preserveZadBarcode(pack?.unitBarcode),
    packagingPackageBarcode: preserveZadBarcode(pack?.packageBarcode),
  }
}
